import React, { useState, useEffect, useRef } from 'react';
import './DatePicker.css';

const DatePicker = ({ 
  value, 
  onChange, 
  name, 
  id, 
  placeholder = 'Select date', 
  className = '', 
  required = false,
  format = 'YYYY-MM-DD', // Default ISO format
  label,
  disabled = false
}) => {
  // State for the calendar visibility
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // State for current month being viewed - always initialize to today
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  
  // State for the selected date (from the value prop)
  const [selectedDate, setSelectedDate] = useState(null);
  
  // State for year dropdown visibility
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  
  // Ref for the calendar popup to handle outside clicks
  const calendarRef = useRef(null);
  
  // Ref for the input container to position the calendar
  const inputContainerRef = useRef(null);
  
  // Ref for the year dropdown to handle scrolling
  const yearDropdownRef = useRef(null);
  
  // State for calendar position
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  
  // Effect to position the calendar when it opens
  useEffect(() => {
    if (isCalendarOpen && inputContainerRef.current) {
      const rect = inputContainerRef.current.getBoundingClientRect();
      const calendarWidth = 290;
      const calendarHeight = 400;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Align left edge of calendar with left edge of input
      let left = rect.left;
      let top = rect.bottom + 8;
      
      // Check if calendar would go off the left edge of the screen
      if (left < 10) {
        left = 10; // 10px margin from edge
      }
      
      // Check if calendar would go off the right edge of the screen
      if (left + calendarWidth > viewportWidth) {
        left = viewportWidth - calendarWidth - 10; // 10px margin from edge
      }
      
      // Always keep calendar below input - don't move above
      // Only adjust if it would go completely off screen
      if (top + calendarHeight > viewportHeight) {
        // Keep below but adjust position to fit as much as possible
        const maxTop = viewportHeight - calendarHeight - 10;
        if (maxTop > rect.bottom + 8) {
          top = maxTop;
        }
        // Otherwise keep it at rect.bottom + 8 and let it overflow (user can scroll page)
      }
      
      setCalendarPosition({
        top: top,
        left: left,
        maxWidth: Math.min(calendarWidth, viewportWidth - 20) // Ensure it fits with margins
      });
    }
  }, [isCalendarOpen]);
  
  // Effect to parse the initial value if any
  useEffect(() => {
    if (value) {
      try {
        // Attempt to parse the date from the value
        const parsedDate = parseInputDate(value);
        if (parsedDate && !isNaN(parsedDate.getTime())) {
          setSelectedDate(parsedDate);
        }
      } catch (error) {
        console.error("Error parsing date:", error);
      }
    }
  }, [value]);
  
  // Effect to handle clicking outside the calendar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
        setIsYearDropdownOpen(false);
      }
    };
    
    // Add event listener when calendar is open
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Reposition calendar on scroll or resize
      const handlePositionChange = () => {
        if (inputContainerRef.current) {
          const rect = inputContainerRef.current.getBoundingClientRect();
          const calendarWidth = 290;
          const calendarHeight = 400;
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Align left edge of calendar with left edge of input
          let left = rect.left;
          let top = rect.bottom + 8;
          
          // Check if calendar would go off the left edge of the screen
          if (left < 10) {
            left = 10; // 10px margin from edge
          }
          
          // Check if calendar would go off the right edge of the screen
          if (left + calendarWidth > viewportWidth) {
            left = viewportWidth - calendarWidth - 10; // 10px margin from edge
          }
          
          // Always keep calendar below input - don't move above
          // Only adjust if it would go completely off screen
          if (top + calendarHeight > viewportHeight) {
            // Keep below but adjust position to fit as much as possible
            const maxTop = viewportHeight - calendarHeight - 10;
            if (maxTop > rect.bottom + 8) {
              top = maxTop;
            }
            // Otherwise keep it at rect.bottom + 8 and let it overflow (user can scroll page)
          }
          
          setCalendarPosition({
            top: top,
            left: left,
            maxWidth: Math.min(calendarWidth, viewportWidth - 20) // Ensure it fits with margins
          });
        }
      };
      
      window.addEventListener('scroll', handlePositionChange);
      window.addEventListener('resize', handlePositionChange);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handlePositionChange);
        window.removeEventListener('resize', handlePositionChange);
      };
    }
    
    // Remove event listener when component unmounts or calendar closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);
  
  // Helper to parse the input date string
  const parseInputDate = (dateString) => {
    if (!dateString) return null;
    
    // Handle different date formats
    let dateParts;
    
    // ISO format (YYYY-MM-DD)
    if (dateString.includes('-')) {
      dateParts = dateString.split('-');
      return new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
    }
    
    // DD/MM/YYYY or MM/DD/YYYY format
    if (dateString.includes('/')) {
      dateParts = dateString.split('/');
      const part1 = parseInt(dateParts[0]);
      const part2 = parseInt(dateParts[1]);
      const year = parseInt(dateParts[2]);
      
      // Determine format by checking if first part could be a day (>12) or if second part is definitely a month (<=12)
      // If first part > 12, it must be DD/MM/YYYY
      // If first part <= 12 and second part <= 12, assume DD/MM/YYYY (European format used in this system)
      if (part1 > 12) {
        // Definitely DD/MM/YYYY (day is > 12)
        return new Date(year, part2 - 1, part1);
      } else {
        // Assume DD/MM/YYYY format (used throughout the system)
        return new Date(year, part2 - 1, part1);
      }
    }
    
    // Try direct parsing as fallback
    return new Date(dateString);
  };
  
  // Helper to format the date for display
  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    if (format === 'MM/DD/YYYY') {
      return `${month}/${day}/${year}`;
    } else if (format === 'DD/MM/YYYY') {
      return `${day}/${month}/${year}`;
    } else {
      // Default to ISO format (YYYY-MM-DD)
      return `${year}-${month}-${day}`;
    }
  };
  
  // Handler for date selection
  const handleDateSelect = (day, month = currentMonth.getMonth(), year = currentMonth.getFullYear()) => {
    const newDate = new Date(year, month, day);
    setSelectedDate(newDate);
    
    // Update the current month being viewed to match the selected date
    setCurrentMonth(new Date(year, month, 1));
    
    // Format the date and call the onChange handler
    const formattedDate = formatDate(newDate);
    if (onChange) {
      const event = {
        target: {
          name,
          value: formattedDate
        }
      };
      onChange(event);
    }
    
    // Close the calendar after selection
    setIsCalendarOpen(false);
  };
  
  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  // Navigate to specific year
  const goToYear = (year) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setIsYearDropdownOpen(false);
  };
  
  // Effect to scroll to current year when dropdown opens
  useEffect(() => {
    if (isYearDropdownOpen && yearDropdownRef.current) {
      const currentYearButton = yearDropdownRef.current.querySelector('.calendar-year-option.selected');
      if (currentYearButton) {
        currentYearButton.scrollIntoView({ behavior: 'instant', block: 'center' });
      }
    }
  }, [isYearDropdownOpen]);
  
  // Generate year options (current year ± 1000 years)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 1000; year <= currentYear + 10; year++) {
      years.push(year);
    }
    return years;
  };
  
  // Toggle calendar visibility
  const toggleCalendar = () => {
    if (!disabled) {
      // If opening the calendar, reset to appropriate month BEFORE opening
      if (!isCalendarOpen) {
        // Always reset to today first
        const today = new Date();
        
        if (selectedDate && value && value.trim() !== '') {
          // Only use selected date if there's an actual non-empty value in the input
          setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
        } else {
          // Always open to TODAY's month if no value
          setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
        }
      }
      setIsCalendarOpen(!isCalendarOpen);
    }
  };
  
  // Handler for manual input in the text field
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // If the input value is empty, clear the selectedDate and pass null to parent
    if (!inputValue || inputValue.trim() === '') {
      setSelectedDate(null);
      
      if (onChange) {
        onChange({
          target: {
            name,
            value: null // Send null instead of empty string to prevent backend validation errors
          }
        });
      }
      return;
    }
    
    // For non-empty values, try to parse the date
    const inputDate = parseInputDate(inputValue);
    if (inputDate && !isNaN(inputDate.getTime())) {
      setSelectedDate(inputDate);
      
      // Format the date according to the specified format
      const formattedDate = formatDate(inputDate);
      
      if (onChange) {
        onChange({
          target: {
            name,
            value: formattedDate
          }
        });
      }
    } else {
      // If date is invalid, still call onChange but with the raw value
      if (onChange) {
        onChange(e);
      }
    }
  };
  
  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Initialize the calendar array with empty cells for the days before the first day of the month
    const calendarDays = Array(firstDayOfWeek).fill(null);
    
    // Add days of the month
    for (let day = 1; day <= totalDays; day++) {
      calendarDays.push(day);
    }
    
    return calendarDays;
  };
  
  // Month names for header
  const monthNames = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];
  
  // Day names for header
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  
  // Get the name of the current month and year
  const currentMonthName = monthNames[currentMonth.getMonth()];
  const currentYear = currentMonth.getFullYear();
  
  // Render the calendar days
  const calendarDays = generateCalendarDays();
  
  // Check if a day is the selected day
  const isSelectedDay = (day) => {
    if (!selectedDate || !day) return false;
    
    return (
      selectedDate.getDate() === day && 
      selectedDate.getMonth() === currentMonth.getMonth() && 
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };
  
  // Check if a day is today
  const isToday = (day) => {
    if (!day) return false;
    
    const today = new Date();
    return (
      today.getDate() === day && 
      today.getMonth() === currentMonth.getMonth() && 
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  return (
    <div className={`date-picker-container ${className} ${isCalendarOpen ? 'calendar-open' : ''}`}>
      {label && <label htmlFor={id || name}>{label} {required && <span className="required-field">*</span>}</label>}
      
      <div className="date-picker-input-container" ref={inputContainerRef}>
        <input
          type="text"
          id={id || name}
          name={name}
          value={value || ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`date-picker-input ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          required={required}
          readOnly={true}
          onClick={toggleCalendar}
        />
        
        <button 
          type="button" 
          onClick={toggleCalendar} 
          className={`date-picker-button ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          aria-label="Select date"
        >
          <CalendarIcon />
        </button>
      </div>
      
      {isCalendarOpen && (
        <div 
          className="date-picker-calendar" 
          ref={calendarRef}
          style={{
            top: `${calendarPosition.top}px`,
            left: `${calendarPosition.left}px`,
            maxWidth: `${calendarPosition.maxWidth}px`
          }}
        >
          <div className="calendar-header">
            <button 
              type="button" 
              className="calendar-nav-button" 
              onClick={goToPreviousMonth}
              aria-label="Previous month"
            >
              <span className="calendar-nav-icon">&#10094;</span>
            </button>
            
            <div className="calendar-header-title">
              <div className="calendar-year-selector">
                <button 
                  type="button" 
                  className="calendar-year-button"
                  onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                  aria-label="Select year"
                >
                  {currentYear}
                </button>
                
                {isYearDropdownOpen && (
                  <div className="calendar-year-dropdown" ref={yearDropdownRef}>
                    {generateYearOptions().map(year => (
                      <button
                        key={year}
                        type="button"
                        className={`calendar-year-option ${year === currentYear ? 'selected' : ''}`}
                        onClick={() => goToYear(year)}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="calendar-month-name">{currentMonthName}</span>
            </div>
            
            <button 
              type="button" 
              className="calendar-nav-button" 
              onClick={goToNextMonth}
              aria-label="Next month"
            >
              <span className="calendar-nav-icon">&#10095;</span>
            </button>
          </div>
          
          <div className="calendar-days-header">
            {dayNames.map((day, index) => (
              <div key={index} className="calendar-day-name">{day}</div>
            ))}
          </div>
          
          <div className="calendar-days-grid">
            {calendarDays.map((day, index) => (
              <div 
                key={index} 
                className={`calendar-day-cell ${!day ? 'empty-cell' : ''} ${isSelectedDay(day) ? 'selected' : ''} ${isToday(day) ? 'today' : ''}`}
                onClick={() => day && handleDateSelect(day)}
              >
                {day}
              </div>
            ))}
          </div>
          
          <div className="calendar-footer">
            <button 
              type="button" 
              className="calendar-today-button" 
              onClick={() => {
                const today = new Date();
                handleDateSelect(today.getDate(), today.getMonth(), today.getFullYear());
              }}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Calendar icon component
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 9H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 8C3 6.34315 4.34315 5 6 5H18C19.6569 5 21 6.34315 21 8V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V8Z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 13H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 13H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 13H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 17H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 17H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export default DatePicker; 